import type { StoryObj } from '@storybook/html'
import type { CobaltBanner as CobaltBannerProps } from './cobalt-banner'
import { ICONS } from '@pollux-docaposte/icon-library'

const iconsAvailable = Object.keys(ICONS)

export default {
  title: 'Components/Feedback/cobalt-banner',
  argTypes: {
    message: {
      type: { name: 'string', required: true },
      description: 'This is the text which will appear inside the banner. It is **required**.',
      table: {
        type: { summary: 'string' }
      }
    },
    headline: {
      type: { name: 'string' },
      description: 'This is the title which will appear on top of the banner.',
      table: {
        type: { summary: 'string' }
      }
    },
    status: {
      type: { name: 'string' },
      options: ['success', 'warning', 'error', 'information', 'neutral'],
      control: { type: 'select' },
      description: 'Allows to change the status of the banner.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'information' }
      }
    },
    iconName: {
      type: { name: 'string' },
      options: iconsAvailable,
      control: { type: 'select' },
      description:
        'Accept all icon names from `@pollux-docaposte/icon-library`. Check icons available in the control column.\nThe icon render only if `status` is "neutral". If status is `neutral` the default icon is `star-solid`',
      table: {
        type: { summary: `${JSON.stringify(iconsAvailable).substring(0, 100)}...` },
        defaultValue: 'star-solid'
      }
    },
    dismiss: {
      control: 'boolean',
      options: [true, false],
      description: 'Show or hide the banner.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    dismissButton: {
      control: 'boolean',
      options: [true, false],
      description: 'Add a button to make the banner dismissable.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    dismissButtonLabel: {
      type: { name: 'string' },
      description: 'This is the label for the close button. It should be used if `dismissButton` is `true`.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'Fermer' }
      }
    }
  }
}

const TemplateDefault = (args: CobaltBannerProps) => {
  return `
<cobalt-banner message="${args.message}" headline="${args.headline}" status="${args.status}" icon-name="${args.iconName}" dismiss="${args.dismiss}" dismiss-button="${args.dismissButton}" dismiss-button-label="${args.dismissButtonLabel}"></cobalt-banner>`
}

export const Default: StoryObj = TemplateDefault.bind({})

Default.args = {
  message: 'Message',
  headline: '',
  status: 'information',
  iconName: undefined,
  dismiss: false,
  dismissButton: false,
  dismissButtonLabel: 'Fermer'
}

const TemplateWithButtons = (args: CobaltBannerProps) => {
  return `
<cobalt-banner message="${args.message}" headline="${args.headline}">
  <div slot="buttons">
    <cobalt-button label="Button" variant="tertiary" size="small"></cobalt-button>
    <cobalt-button label="Button" variant="tertiary" size="small"></cobalt-button>
  </div>
</cobalt-banner>`
}

export const WithButtons: StoryObj = TemplateWithButtons.bind({})

WithButtons.args = {
  message: 'Message',
  headline: 'Headline'
}

const TemplateWithInlineButtons = (args: CobaltBannerProps) => {
  return `
<cobalt-banner message="${args.message}" headline="${args.headline}" status="${args.status}" icon-name="${args.iconName}" dismiss="${args.dismiss}" dismiss-button="${args.dismissButton}" dismiss-button-label="${args.dismissButtonLabel}" inline-buttons="${args.inlineButtons}">
  <div slot="buttons">
    <cobalt-button label="Button" variant="tertiary" size="small"></cobalt-button>
    <cobalt-button label="Button" variant="tertiary" size="small"></cobalt-button>
  </div>
</cobalt-banner>`
}

export const WithInlineButtons: StoryObj = TemplateWithInlineButtons.bind({})

WithInlineButtons.args = {
  message: 'Message',
  headline: 'Headline',
  status: 'information',
  inlineButtons: true,
  iconName: undefined,
  dismiss: false,
  dismissButton: true,
  dismissButtonLabel: 'Fermer'
}
